import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { auditChainAPI, governanceAPI, observabilityAPI } from '../../lib/api';
import { ShieldCheck, KeyRound, Trash2, AlertCircle, RefreshCw, Activity, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminGovernance() {
  const [chain, setChain] = useState(null);
  const [secrets, setSecrets] = useState([]);
  const [retention, setRetention] = useState(null);
  const [heartbeats, setHeartbeats] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      auditChainAPI.verify(500),
      governanceAPI.secretsRotation(),
      governanceAPI.retentionDryRun(),
      observabilityAPI.heartbeats('backup'),
    ])
      .then(([c, s, r, h]) => {
        setChain(c.data);
        setSecrets(s.data?.secrets || []);
        setRetention(r.data);
        setHeartbeats(h.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const purge = async () => {
    if (!window.confirm('Run the retention purge now? Audit logs are retained.')) return;
    try {
      const r = await governanceAPI.retentionRun();
      toast.success(`Purged ${Object.values(r.data.purged || {}).reduce((s, n) => s + (n || 0), 0)} records`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    }
  };

  const rotateSecret = async (name) => {
    try {
      await governanceAPI.markSecretRotated(name);
      toast.success(`Marked ${name} rotated`);
      refresh();
    } catch {
      toast.error('Failed to record rotation');
    }
  };

  if (loading) return <Skeleton className="h-72 rounded-xl" />;

  return (
    <div className="space-y-6" data-testid="admin-governance-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#B89B5E]" /> Compliance &amp; Governance</h1>
        <p className="text-sm text-[#5B616E] mt-1">Audit-chain integrity, retention purges, secret rotation, and backup heartbeats — all in one place.</p>
      </div>

      {/* Audit chain */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Tamper-evident audit chain</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => auditChainAPI.download('csv').catch(() => toast.error('Export failed'))}
              data-testid="audit-export-csv"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => auditChainAPI.download('json').catch(() => toast.error('Export failed'))}
              data-testid="audit-export-json"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Export JSON
            </Button>
            <Button variant="outline" size="sm" onClick={refresh}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-verify</Button>
          </div>
        </CardHeader>
        <CardContent>
          {chain ? (
            <div className="text-sm space-y-2">
              <p>
                {chain.is_intact ? (
                  <Badge className="bg-[#1F7A5A] text-white">Chain intact</Badge>
                ) : (
                  <Badge className="bg-[#B3261E] text-white">Chain broken</Badge>
                )}
                <span className="ml-3 text-xs text-[#5B616E]">{chain.verified} of {chain.examined} entries verified · head <code className="text-[11px]">{chain.head_hash?.slice(0, 12) || '—'}</code></span>
              </p>
              {(chain.breaks || []).length > 0 && (
                <div className="text-xs text-[#B3261E]">
                  <p className="font-medium flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Anomalies detected:</p>
                  <ul className="ml-5 list-disc">
                    {(chain.breaks || []).slice(0, 5).map((b, i) => <li key={i}>{b.action} ({b.id})</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : <p className="text-xs text-[#5B616E]">Could not load chain.</p>}
        </CardContent>
      </Card>

      {/* Secrets rotation */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><KeyRound className="w-4 h-4" /> Secrets rotation (90-day policy)</CardTitle></CardHeader>
        <CardContent>
          <div className="divide-y divide-[#E7E0D6]">
            {secrets.map((s) => (
              <div key={s.name} className="flex items-center gap-3 py-2 text-sm">
                <span className="font-mono w-44">{s.name}</span>
                <Badge variant="outline" className={`text-[10px] ${s.status === 'stale' ? 'border-[#B3261E] text-[#B3261E]' : 'border-[#1F7A5A] text-[#1F7A5A]'}`}>
                  {s.status}
                </Badge>
                <span className="text-xs text-[#5B616E]">
                  {s.age_days < 0 ? 'Never recorded' : `${s.age_days}d ago`}
                </span>
                <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={() => rotateSecret(s.name)}>
                  Mark rotated
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Retention */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><Trash2 className="w-4 h-4" /> Data retention</CardTitle>
          <Button size="sm" className="bg-[#121316] text-white hover:bg-[#1B1D22]" onClick={purge}>Run purge</Button>
        </CardHeader>
        <CardContent>
          {retention ? (
            <div className="text-sm space-y-1">
              <p className="text-xs text-[#5B616E]">Cut-off: <code>{retention.cutoff}</code> ({retention.retention_days} days)</p>
              {Object.entries(retention.would_purge || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-xs">
                  <span className="font-mono w-44">{k}</span>
                  <span className="font-medium">{v} records</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-[#5B616E]">Could not load retention.</p>}
        </CardContent>
      </Card>

      {/* Heartbeats */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4" /> Backup heartbeats</CardTitle></CardHeader>
        <CardContent>
          {heartbeats ? (
            <>
              <p className="text-xs text-[#5B616E]">Last successful backup: {heartbeats.last_ok_age_minutes != null ? `${heartbeats.last_ok_age_minutes} min ago` : 'never'} {heartbeats.alert && <Badge className="ml-2 bg-[#B3261E] text-white">Stale</Badge>}</p>
              <div className="mt-2 space-y-0.5 text-xs max-h-48 overflow-auto">
                {(heartbeats.heartbeats || []).map((h) => (
                  <div key={h.id} className="flex gap-2">
                    <span className="text-[10px] text-[#5B616E]">{new Date(h.created_at).toLocaleString()}</span>
                    <Badge variant="outline" className={`text-[10px] ${h.ok ? 'border-[#1F7A5A] text-[#1F7A5A]' : 'border-[#B3261E] text-[#B3261E]'}`}>
                      {h.ok ? 'OK' : 'FAIL'}
                    </Badge>
                    <span className="truncate">{h.message}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-xs text-[#5B616E]">No heartbeats recorded.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
